import { useCallback } from "react";
import { useCurrentUser } from "./useCurrentUser";
import { useUserPreferences, useSetUserPreferences } from "./useUserPreferences";
import { useAvailableModels } from "./useAvailableModels";
import { DEFAULT_MODEL_PREF_KEY, mergePreference, resolveDefaultModel } from "../lib/defaultModel";

/**
 * The user's chosen default model, persisted per user.
 *
 * Stored in the same preferences blob as app order and hidden links, keyed by email,
 * so it follows the person across devices and respects admin impersonation the same
 * way every other preference does.
 */
export function useDefaultModel() {
  const { user: currentUser } = useCurrentUser();
  const userId = currentUser?.email_address ?? "";
  const { data: preferences } = useUserPreferences(userId);
  const setPreferences = useSetUserPreferences();
  const { models, defaultModelId: workerDefault } = useAvailableModels();

  const saved = preferences?.[DEFAULT_MODEL_PREF_KEY] as string | undefined;
  const defaultModel = resolveDefaultModel(saved, workerDefault, models);

  const setDefaultModel = useCallback(
    (modelId: string) => {
      if (!userId) return;
      // Read-modify-write: the API replaces the whole blob, so anything not carried
      // forward here is lost.
      setPreferences.mutate({
        userId,
        preferences: mergePreference(preferences, DEFAULT_MODEL_PREF_KEY, modelId),
      });
    },
    [userId, preferences, setPreferences],
  );

  return {
    defaultModel,
    setDefaultModel,
    /** True while we have no user yet — the setter is a no-op until then. */
    isReady: Boolean(userId),
    isSaving: setPreferences.isPending,
    /** What the worker would use if the user expressed no preference. */
    workerDefault,
  };
}
